import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'

import style from './ConfirmDialog.module.css'

interface IDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirmClick: () => void | Promise<void>
  title: ReactNode
  titleRight?: ReactNode
  className?: string
  cancelText?: string
  confirmText?: string
  danger?: boolean
  confirmButtonDisabled?: boolean
  /** Stack nested dialogs above the default overlay (default 100). */
  overlayZIndex?: number
  showCancelButton?: boolean
  /** Shown above the body while the dialog stays open (e.g. mode-switch timeout). */
  error?: string | null
  /** External in-flight wait (e.g. stop mode confirmation) — spinner on confirm, cancel still aborts. */
  busy?: boolean
  showConfirmButton?: boolean
}

const ConfirmDialog = (props: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const [actionInProgress, setActionInProgress] = useState(false)
  const busy = Boolean(props.busy) || actionInProgress

  // Exit fullscreen when modal opens — the Fullscreen API top-layer
  // hides all elements outside the fullscreen container, so portaled
  // modals are invisible. Exiting is the only reliable workaround.
  useEffect(() => {
    if (props.isOpen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    }
  }, [props.isOpen])

  const onAction = async () => {
    try {
      setActionInProgress(true)
      await props.onConfirmClick()
      setActionInProgress(false)
    } catch (error) {
      console.log(error)
    }
    setActionInProgress(false)
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={false}
      style={{
        overlay: {
          zIndex: props.overlayZIndex ?? 100,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.title}</div>
        {props.titleRight && <div className={style.titleRight}>{props.titleRight}</div>}
      </div>
      {props.error && <div className={style.errorAlert}>{props.error}</div>}
      {props.children}
      <div className={style.footer}>
        {(props.showCancelButton ?? true) && (
          <Button.Outlined className={style.btn} onClick={props.onClose}>
            {props.cancelText || t('common.cancel')}
          </Button.Outlined>
        )}
        {(props.showConfirmButton ?? true) && (
          <Button.Standart
            className={props.danger ? clsx(style.btn, style.btnRed) : style.btn}
            disabled={busy || props.confirmButtonDisabled}
            loading={busy}
            onClick={onAction}
          >
            {props.confirmText || t('common.ok')}
          </Button.Standart>
        )}
      </div>
    </Modal>
  )
}

export default ConfirmDialog
